import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Search, Eye, MoreVertical, Receipt, Loader2, IndianRupee, FileDown, FileSpreadsheet, FileText, ArrowRightCircle, CheckCircle2, Trash2, Mail, AlertTriangle, Plus, Pencil, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { exportToExcel } from '../lib/exportUtils';
import { useConfirmation } from '../components/ConfirmationProvider';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatTimeIST } from '../lib/formatUtils';
import { TransactionFormDialog } from '../components/transactions/TransactionFormDialog';
import { EditInvoiceDialog } from '../components/invoices/EditInvoiceDialog';
import TruncatedText from '../components/common/TruncatedText';
import { Pagination } from '../components/Pagination';
import { usePageSize } from '../context/PageSizeContext';
import { useDebounce } from '../lib/useDebounce';
import { ClientLink, ProjectLink } from '../components/links/EntityLinks';

const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt || 0);

const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'paid', label: 'Paid' },
  { value: 'converted', label: 'Converted' },
  { value: 'cancelled', label: 'Cancelled' },
];

export const InvoicesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const confirm = useConfirmation();
  const { pageSize } = usePageSize();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('invoices.edit');
  const canDelete = hasPermission('invoices.delete');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  // Initialize from `?search=` so deep-links from ClientDetailPage land on
  // the correct invoice immediately.
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') || '');
  const debouncedSearch = useDebounce(searchQuery, 350);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editInvoice, setEditInvoice] = useState(null);
  const [txnDialogOpen, setTxnDialogOpen] = useState(false);
  const [txnInitial, setTxnInitial] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Reset to page 1 whenever filters / search change
  useEffect(() => { setPage(1); }, [statusFilter, typeFilter, dateFrom, dateTo, debouncedSearch, pageSize]);

  useEffect(() => { fetchInvoices(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ },
    [statusFilter, typeFilter, dateFrom, dateTo, debouncedSearch, page, pageSize]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      // Server-side filters (status + invoice_date range + invoice_type +
      // search). The generic CRUD honors `<field>_gte / <field>_lte` for any
      // `filter_fields` entry — see backend/server.py:invoices.
      const params = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
        sort_by: 'invoice_date',
        sort_order: 'desc',
      });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (typeFilter !== 'all') params.set('invoice_type', typeFilter);
      if (dateFrom) params.set('invoice_date_gte', dateFrom);
      if (dateTo) params.set('invoice_date_lte', `${dateTo}T23:59:59`);
      if (debouncedSearch) params.set('search', debouncedSearch);
      const response = await api.get(`/invoices?${params.toString()}`);
      if (response.data.success) {
        setInvoices(response.data.data);
        setTotal(response.data.total ?? response.data.data.length);
      }
    } catch {
      setInvoices([]);
      setTotal(0);
    } finally { setLoading(false); }
  };

  const handleConvertToTax = async (inv) => {
    const confirmed = await confirm({
      title: 'Convert to Tax Invoice',
      description: `This will generate a Tax Invoice from Proforma ${inv.invoice_number}. The proforma will be marked as "Converted".`,
      confirmText: 'Convert',
    });
    if (!confirmed) return;
    try {
      const res = await api.post(`/invoices/${inv.id}/convert-to-tax`);
      if (res.data.success) {
        toast.success(res.data.message);
        // Auto-render the new tax invoice PDF on the server
        try { await _generateAndStorePDF(res.data.data); } catch {}
        fetchInvoices();
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Conversion failed'); }
  };

  const handleMarkPaid = async (inv) => {
    const confirmed = await confirm({
      title: 'Mark as Paid',
      description: `Mark tax invoice ${inv.invoice_number} as paid?`,
      confirmText: 'Mark Paid',
    });
    if (!confirmed) return;
    try {
      await api.post(`/invoices/${inv.id}/mark-paid`);
      toast.success('Invoice marked as paid');
      fetchInvoices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (inv) => {
    const confirmed = await confirm({ title: 'Delete Invoice', description: `Delete ${inv.invoice_number}? This cannot be undone.` });
    if (!confirmed) return;
    try { await api.delete(`/invoices/${inv.id}`); toast.success('Deleted'); fetchInvoices(); }
    catch { toast.error('Delete failed'); }
  };

  const handleAddTransaction = (inv) => {
    setTxnInitial({
      type: 'credit',
      project_id: inv.project_id || '',
      project_name: inv.project_name || '',
      client_name: inv.client_name || '',
      invoice_id: inv.id,
      invoice_number: inv.invoice_number,
      invoice_subtotal: inv.subtotal || inv.total || 0,
      invoice_total: inv.total || 0,
      invoice_type: inv.invoice_type || '',
      invoice_status: inv.status || '',
      bank_account: inv.bank_account_id || '',
    });
    setTxnDialogOpen(true);
  };

  /**
   * Server-side render the PDF (mirrors the webhook-generated layout)
   * and persist it for email use. Returns true on success.
   */
  const _generateAndStorePDF = async (inv) => {
    try {
      await api.post(`/invoices/${inv.id}/regenerate-pdf`);
      return true;
    } catch (e) {
      console.warn('PDF generation skipped:', e?.response?.data || e?.message);
      return false;
    }
  };

  const handleDownloadPDF = async (inv) => {
    try {
      // Ask backend to (re)render the PDF, then stream the saved file.
      const ok = await _generateAndStorePDF(inv);
      if (!ok) { toast.error('PDF generation failed'); return; }
      const resp = await api.get(`/invoices/${inv.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(inv.invoice_number || inv.id).replace(/\//g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error('PDF generation error:', err); toast.error('PDF generation failed'); }
  };

  const handleSendTaxInvoice = async (inv) => {
    try {
      await _generateAndStorePDF(inv);
      const r = await api.post(`/invoices/${inv.id}/send-tax-invoice`);
      const status = r.data?.data?.status;
      if (status === 'sent') toast.success('Tax invoice email sent');
      else if (status === 'skipped_no_recipients') toast.warning('No billing emails configured on this client. Add them on the Clients page first.');
      else if (status === 'skipped_no_smtp') toast.error('SMTP not configured. Configure email settings in Settings.');
      else if (status === 'failed') toast.error(`Send failed: ${r.data?.data?.log?.error || 'Unknown error'}`);
      else toast.info(`Email status: ${status}`);
      fetchInvoices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Send failed'); }
  };

  // Email with Pay-Now (Razorpay) — generates a fresh payment link (or
  // reuses an open one), then emails the PDF + Pay-Now CTA to the
  // client's billing addresses. Available on proforma (any non-final
  // status) + unpaid tax invoices.
  const handleSendWithPayLink = async (inv) => {
    try {
      await _generateAndStorePDF(inv);
      const r = await api.post(`/invoices/${inv.id}/send-with-payment-link`);
      const emailStatus = r.data?.data?.email?.status;
      if (emailStatus === 'sent') {
        toast.success('Invoice + Pay-Now link emailed to client');
      } else if (emailStatus === 'skipped_no_recipients') {
        toast.warning('No billing emails configured on this client.');
      } else if (emailStatus === 'skipped_no_smtp') {
        toast.error('SMTP not configured. Configure email settings in Settings.');
      } else if (emailStatus === 'failed') {
        toast.error(`Send failed: ${r.data?.data?.email?.log?.error || 'Unknown error'}`);
      } else {
        toast.info(`Status: ${emailStatus}`);
      }
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invoice with Pay-Now link');
    }
  };

  const getStatusBadge = (inv) => {
    const isProforma = inv.invoice_type === 'proforma';
    const status = inv.status;
    if (status === 'converted') return <Badge className="bg-violet-500/10 text-violet-500">Converted</Badge>;
    if (status === 'paid') return <Badge className="bg-emerald-500/10 text-emerald-500">Paid</Badge>;
    if (status === 'unpaid') return <Badge className="bg-amber-500/10 text-amber-500">Unpaid</Badge>;
    if (status === 'draft') return <Badge className="bg-gray-500/10 text-gray-500">Draft</Badge>;
    if (status === 'cancelled') return <Badge className="bg-red-500/10 text-red-500">Cancelled</Badge>;
    return <Badge className="bg-gray-500/10 text-gray-500">{status}</Badge>;
  };

  const getTypeBadge = (type) => {
    if (type === 'proforma') return <Badge className="bg-amber-500/10 text-amber-500 font-mono text-[10px]">PI</Badge>;
    if (type === 'tax') return <Badge className="bg-blue-500/10 text-blue-500 font-mono text-[10px]">TAX</Badge>;
    return <Badge className="bg-gray-500/10 text-gray-400 font-mono text-[10px]">Legacy</Badge>;
  };

  const filtered = invoices;

  const totalProforma = invoices.filter(i => i.invoice_type === 'proforma' && i.status !== 'converted').reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
  const totalUnpaid = invoices.filter(i => i.status === 'unpaid').reduce((s, i) => s + (parseFloat(i.total) || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="invoices-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Invoices</h1>
          <p className="text-muted-foreground">Manage proforma and tax invoices</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => exportToExcel(invoices, [
            { key: 'invoice_number', header: 'Invoice #' },
            { key: 'invoice_type', header: 'Type' },
            { key: 'client_name', header: 'Client' },
            { key: 'project_name', header: 'Project' },
            { key: 'subtotal', header: 'Amount', format: v => formatCurrency(v || 0) },
            { key: 'tax_amount', header: 'GST', format: v => formatCurrency(v || 0) },
            { key: 'total', header: 'Total', format: v => formatCurrency(v || 0) },
            { key: 'status', header: 'Status' },
            { key: 'created_by_name', header: 'Created By' },
          ], 'Invoices')} data-testid="export-invoices-excel"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
        </div>
      </div>

      <Card><CardContent className="py-4"><div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Search invoices..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-testid="invoice-search" /></div>
        <Select value={typeFilter} onValueChange={setTypeFilter}><SelectTrigger className="w-[140px]" data-testid="invoice-type-filter"><SelectValue placeholder="Type" /></SelectTrigger><SelectContent><SelectItem value="all">All Types</SelectItem><SelectItem value="proforma">Proforma</SelectItem><SelectItem value="tax">Tax</SelectItem></SelectContent></Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger className="w-[140px]" data-testid="invoice-status-filter"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="all">All Status</SelectItem>{statusOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select>
        <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-[160px]" placeholder="From" data-testid="invoice-date-from" />
        <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-[160px]" placeholder="To" data-testid="invoice-date-to" />
        {(dateFrom || dateTo || statusFilter !== 'all' || typeFilter !== 'all') && (
          <Button variant="ghost" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); setStatusFilter('all'); setTypeFilter('all'); }} data-testid="invoice-clear-filters">
            Clear
          </Button>
        )}
      </div></CardContent></Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center"><Receipt className="h-5 w-5 text-primary" /></div><div><p className="text-2xl font-bold" data-testid="invoices-total-count">{total}</p><p className="text-xs text-muted-foreground">Total</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center"><IndianRupee className="h-5 w-5 text-emerald-500" /></div><div><p className="text-xl font-bold">{formatCurrency(totalPaid)}</p><p className="text-xs text-muted-foreground">Paid</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center"><IndianRupee className="h-5 w-5 text-amber-500" /></div><div><p className="text-xl font-bold">{formatCurrency(totalUnpaid)}</p><p className="text-xs text-muted-foreground">Unpaid</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-violet-500/10 flex items-center justify-center"><FileText className="h-5 w-5 text-violet-500" /></div><div><p className="text-xl font-bold">{formatCurrency(totalProforma)}</p><p className="text-xs text-muted-foreground">Proforma</p></div></div></CardContent></Card>
      </div>

      <Card><CardContent className="p-0">
        {loading ? <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> :
        filtered.length === 0 ? <div className="flex flex-col items-center justify-center h-64 text-muted-foreground"><Receipt className="h-12 w-12 mb-4 opacity-50" /><p>No invoices found</p><p className="text-sm mt-1">Raise an invoice from the Projects page</p></div> :
        <Table data-testid="invoices-table"><TableHeader><TableRow><TableHead>Invoice #</TableHead><TableHead>Type</TableHead><TableHead>Client</TableHead><TableHead>Project</TableHead><TableHead>Amount</TableHead><TableHead>GST</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead><TableHead className="text-xs">Created By</TableHead><TableHead className="w-[80px]">Actions</TableHead></TableRow></TableHeader>
        <TableBody>{filtered.map(inv => (
          <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
            <TableCell className="font-mono text-sm font-medium">
              <div className="flex items-center gap-1.5">
                <span>{inv.invoice_number || `#${inv.id?.slice(0,8)}`}</span>
                {inv.invoice_type === 'tax' && inv.payment_email_status === 'sent' && (
                  <span title={`Tax invoice email sent ${inv.payment_email_sent_at ? 'on ' + new Date(inv.payment_email_sent_at).toLocaleDateString() : ''}`}>
                    <Mail className="h-3 w-3 text-emerald-500" />
                  </span>
                )}
                {inv.invoice_type === 'tax' && ['skipped_no_recipients','skipped_pdf_stale','skipped_no_pdf','failed'].includes(inv.payment_email_status) && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleSendTaxInvoice(inv); }}
                    title={`Email pending: ${inv.payment_email_status.replace(/_/g, ' ')} — click to retry`}
                    className="hover:bg-amber-500/10 rounded p-0.5 transition-colors"
                    data-testid={`retry-email-${inv.id}`}
                  >
                    <AlertTriangle className="h-3 w-3 text-amber-500" />
                  </button>
                )}
              </div>
            </TableCell>
            <TableCell>{getTypeBadge(inv.invoice_type)}</TableCell>
            <TableCell className="font-medium max-w-[140px]">
              <TruncatedText text={inv.client_name}>
                <ClientLink id={inv.client_id} name={inv.client_name} />
              </TruncatedText>
            </TableCell>
            <TableCell className="text-sm text-muted-foreground max-w-[120px]">
              <TruncatedText text={inv.project_name}>
                <ProjectLink id={inv.project_id} name={inv.project_name} fallback="-" />
              </TruncatedText>
            </TableCell>
            <TableCell className="font-mono text-sm">{formatCurrency(inv.subtotal || inv.amount)}</TableCell>
            <TableCell className="font-mono text-sm">{formatCurrency(inv.tax_amount || 0)}</TableCell>
            <TableCell className="font-mono text-sm font-semibold">{formatCurrency(inv.total || inv.amount)}</TableCell>
            <TableCell>{getStatusBadge(inv)}</TableCell>
            <TableCell className="text-sm">
              <div>{formatDate(inv.invoice_date)}</div>
              {inv.created_at && (
                <div className="text-[10px] text-muted-foreground"
                     data-testid={`inv-time-${inv.id}`}>
                  {formatTimeIST(inv.created_at)}
                </div>
              )}
            </TableCell>
            <TableCell>
              <span
                className={`text-[11px] ${inv.created_by_name?.startsWith('System') || inv.created_by_name?.startsWith('Customer') ? 'text-muted-foreground italic' : 'text-foreground'}`}
                data-testid={`inv-created-by-${inv.id}`}
                title={inv.created_by || 'Unknown'}
              >
                {inv.created_by_name || '—'}
              </span>
            </TableCell>
            <TableCell>
              <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`invoice-actions-${inv.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => { setSelectedInvoice(inv); setIsViewOpen(true); }}><Eye className="h-4 w-4 mr-2" />View Details</DropdownMenuItem>
                {canEdit && (
                <DropdownMenuItem onClick={() => setEditInvoice(inv)} data-testid={`edit-invoice-${inv.id}`}>
                  <Pencil className="h-4 w-4 mr-2" />Edit Invoice
                </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => handleDownloadPDF(inv)}><FileDown className="h-4 w-4 mr-2" />Download PDF</DropdownMenuItem>
                {canEdit && ((inv.invoice_type === 'proforma' && inv.status !== 'cancelled') || (inv.invoice_type === 'tax' && inv.status !== 'paid' && inv.status !== 'cancelled')) && (
                  <DropdownMenuItem onClick={() => handleAddTransaction(inv)} data-testid={`add-txn-${inv.id}`}>
                    <Plus className="h-4 w-4 mr-2" />Add Transaction
                  </DropdownMenuItem>
                )}
                {inv.invoice_type === 'tax' && (
                  <DropdownMenuItem onClick={() => handleSendTaxInvoice(inv)} data-testid={`send-email-${inv.id}`}>
                    <Mail className="h-4 w-4 mr-2" />
                    {(() => {
                      const st = inv.payment_email_status;
                      if (st === 'sent') return 'Resend Tax Invoice Email';
                      if (['skipped_no_pdf','skipped_pdf_stale','skipped_no_recipients','failed'].includes(st)) return 'Retry Email Send';
                      return 'Send Tax Invoice Email';
                    })()}
                  </DropdownMenuItem>
                )}
                {/* Email with Pay-Now — proforma (any non-final status)
                    OR unpaid tax invoices. Hidden on paid/cancelled/
                    converted invoices since there's nothing to collect. */}
                {canEdit && (
                  (inv.invoice_type === 'proforma' && !['cancelled', 'converted'].includes(inv.status))
                  || (inv.invoice_type === 'tax' && inv.status === 'unpaid')
                ) && (
                  <DropdownMenuItem
                    onClick={() => handleSendWithPayLink(inv)}
                    data-testid={`send-pay-link-${inv.id}`}
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    {inv.payment_request_status === 'sent' ? 'Resend with Pay-Now' : 'Email with Pay-Now'}
                  </DropdownMenuItem>
                )}
                {canEdit && inv.invoice_type === 'proforma' && inv.status !== 'converted' && (
                  <DropdownMenuItem onClick={() => handleConvertToTax(inv)}><ArrowRightCircle className="h-4 w-4 mr-2" />Convert to Tax Invoice</DropdownMenuItem>
                )}
                {canEdit && inv.invoice_type === 'tax' && inv.status !== 'paid' && (
                  <DropdownMenuItem onClick={() => handleMarkPaid(inv)}><CheckCircle2 className="h-4 w-4 mr-2" />Mark as Paid</DropdownMenuItem>
                )}
                {canDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handleDelete(inv)} className="text-destructive focus:text-destructive" data-testid={`delete-invoice-${inv.id}`}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
                </>
                )}
              </DropdownMenuContent></DropdownMenu>
            </TableCell>
          </TableRow>))}</TableBody></Table>}
      </CardContent></Card>

      {!loading && total > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          testIdPrefix="invoices-pagination"
        />
      )}

      {/* View Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Invoice Details</DialogTitle></DialogHeader>
          {selectedInvoice && <div className="space-y-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-mono text-lg font-bold">{selectedInvoice.invoice_number}</p>
                <p className="text-sm text-muted-foreground"><ClientLink id={selectedInvoice.client_id} name={selectedInvoice.client_name} /></p>
                {selectedInvoice.project_name && <p className="text-xs text-muted-foreground">Project: <ProjectLink id={selectedInvoice.project_id} name={selectedInvoice.project_name} /></p>}
              </div>
              <div className="flex gap-2">
                {getTypeBadge(selectedInvoice.invoice_type)}
                {getStatusBadge(selectedInvoice)}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm pt-2 border-t border-border">
              <div><p className="text-muted-foreground">Description</p><p className="font-medium">{selectedInvoice.description || 'N/A'}</p></div>
              <div><p className="text-muted-foreground">SAC Code</p><p className="font-medium font-mono">{selectedInvoice.sac_code} - {selectedInvoice.sac_label}</p></div>
              <div><p className="text-muted-foreground">Subtotal</p><p className="font-semibold">{formatCurrency(selectedInvoice.subtotal)}</p></div>
              <div><p className="text-muted-foreground">GST ({selectedInvoice.tax_percent ?? 0}%)</p><p className="font-semibold">{formatCurrency(selectedInvoice.tax_amount)}</p></div>
              <div><p className="text-muted-foreground">Total</p><p className="font-semibold text-primary">{formatCurrency(selectedInvoice.total)}</p></div>
              <div><p className="text-muted-foreground">Date</p><p className="font-medium">{formatDate(selectedInvoice.invoice_date)}</p></div>
            </div>
            {selectedInvoice.invoice_type === 'tax' && !selectedInvoice.payment_received && (
              <div className="p-2 rounded bg-amber-500/10 text-center text-xs text-amber-500 font-medium">Payment not yet received</div>
            )}
            {selectedInvoice.converted_from_proforma_id && (
              <div className="p-2 rounded bg-blue-500/10 text-center text-xs text-blue-500">Converted from proforma</div>
            )}
          </div>}
        </DialogContent>
      </Dialog>

      <TransactionFormDialog
        open={txnDialogOpen}
        onClose={() => { setTxnDialogOpen(false); setTxnInitial(null); }}
        initialValues={txnInitial}
        onSaved={() => fetchInvoices()}
      />

      <EditInvoiceDialog
        invoice={editInvoice}
        open={!!editInvoice}
        onClose={() => setEditInvoice(null)}
        onSaved={fetchInvoices}
      />
    </div>
  );
};
